import React, { useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import Search from '../../componentsV2/SearchBar/Search';
import { useFetchContacts } from '../../utils/api/contact';
import { useToasts } from '../../hooks';
import { DialogsProvider, ToastProvider } from '../../providers';
import { withContextProvider } from '../../hoc';
import Breadcrumbs from '../../componentsV2/Breadcrumbs/Breadcrumbs';
import ContactsPage from './ContactsPage';
import { updateSearchParamsAndUrl } from '../../utils/updateSearchParamsAndUrl';
import useSyncedSearchParams from '../../hooks/useSyncedSearchParams';
import { parseParams } from '../../utils/parseParams';

const parseContactParams = (searchParams) => parseParams(searchParams, {
  name: {},
  contactType: {},
  company: {},
  relatedSites: {},
  priority: {},
  h2a: {},
  sortBy: {},
  direction: {},
  perPage: { type: 'int', default: 20 },
  page: { type: 'int', default: 1 },
});

const getContactPage = (searchParams) => parseInt(searchParams.get('page'), 10) || 1;

const DEFAULT_INITIAL_MODEL_FORM_ITEMS = [];

const ContactsPageWrapper = ({ organizationId, initialModelFormItems = DEFAULT_INITIAL_MODEL_FORM_ITEMS }) => {
  const { showToast } = useToasts();
  const defaultItemsPerPage = 20;

  const {
    params, setParams, currentPage, setCurrentPage,
  } = useSyncedSearchParams(parseContactParams, getContactPage);

  const itemsPerPage = params.perPage || defaultItemsPerPage;
  const [contacts, setContacts] = useState([]);
  const [totalContacts, setTotalContacts] = useState(null);
  const [totalUnfilteredContacts, setTotalUnfilteredContacts] = useState(null);
  const [isNewContact, setIsNewContact] = useState(false);
  const isContactsLoading = totalContacts === null || totalUnfilteredContacts === null;
  const isSearchActive = !!params.name || !!params.company || !!params.contactType;
  const fetchContacts = useFetchContacts({
    organizationId, params, setContacts, setTotalContacts, setTotalUnfilteredContacts, showToast,
  });

  // fetch contacts on page load or when params change
  useEffect(() => {
    setTotalContacts(null);
    setTotalUnfilteredContacts(null);
    fetchContacts();
  }, [fetchContacts]);

  // update contacts when added a new contact
  useEffect(() => {
    if (isNewContact) {
      // Return to page 1 after creating a new contact
      setCurrentPage(1);
      fetchContacts();
      setIsNewContact(false);
    }
  }, [fetchContacts, isNewContact, setCurrentPage]);

  const onPageChange = (page) => {
    const updatedParams = {
      ...params,
      page,
      perPage: itemsPerPage,
    };

    setCurrentPage(page);
    setParams(updatedParams);

    updateSearchParamsAndUrl({
      params: updatedParams,
    });
  };

  const onSearch = (searchValues) => {
    const updatedParams = {
      ...params,
      ...searchValues,
      page: 1,
      perPage: itemsPerPage,
    };

    setCurrentPage(1);
    setParams(updatedParams);

    updateSearchParamsAndUrl({
      params: updatedParams,
    });
  };

  const onSort = (values) => {
    const updatedParams = {
      ...params,
      sortBy: values.field,
      direction: values.order,
      page: 1,
      perPage: itemsPerPage,
    };

    setCurrentPage(1);
    setParams(updatedParams);

    updateSearchParamsAndUrl({
      params: updatedParams,
    });
  };

  const breadcrumbLinks = [
    { href: '/sites', name: 'Sites' },
    { href: '/contacts', name: 'Contacts' },
  ];

  const mappedActiveSearchParams = {
    name: params.name || '',
    company: params.company || '',
    contactType: params.contactType || '',
  };

  const searchFields = [
    { label: 'Name', name: 'name', type: 'text' },
    { label: 'Company', name: 'company', type: 'text' },
    {
      label: 'Type', name: 'contactType', type: 'dropdown', options: ['Grower', 'Contractor', 'Worker', 'Partner Organization', 'School/Daycare', 'Other'], shouldShowClearOption: true, clearOptionLabel: 'All', dropdownPlaceholder: 'All',
    },
  ];

  const chipsSettings = {
    name: {
      prefix: 'name',
      type: 'text',
    },
    company: {
      prefix: 'company',
      type: 'text',
    },
    contactType: {
      prefix: 'contact type',
      type: 'text',
    },
  };

  return (
    <DialogsProvider>
      <div className="ml-2.75">
        <header>
          <div className="flex justify-between items-start w-full">
            {/* Breadcrumbs */}
            <Breadcrumbs links={breadcrumbLinks} />
            {/* Search Section */}
            <div className="self-start">
              <Search
                onSearch={onSearch}
                fields={searchFields}
                title="search contacts"
                chipsSettings={chipsSettings}
                activeSearchParams={mappedActiveSearchParams}
              />
            </div>
          </div>
        </header>
        <ContactsPage
          organizationId={organizationId}
          initialModelFormItems={initialModelFormItems}
          currentPage={currentPage}
          totalContacts={totalContacts}
          totalUnfilteredContacts={totalUnfilteredContacts}
          contacts={contacts}
          setIsNewContact={setIsNewContact}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
          showToast={showToast}
          onSort={onSort}
          isLoading={isContactsLoading}
          isSearchActive={isSearchActive}
          params={params}
        />
      </div>
    </DialogsProvider>
  );
};

ContactsPageWrapper.propTypes = {
  organizationId: PropTypes.number.isRequired,
  initialModelFormItems: PropTypes.array,
};

export default withContextProvider(ToastProvider)(ContactsPageWrapper);
